import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ChangeEvent, ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/api/auth'
import { isApiError } from '@/api/client'
import { useJoinChannel } from '@/api/queries'
import type { Room } from '@/api/types'
import { Button, Modal, TextField, useToast } from '@/components/ui'
import styles from './ChannelBrowser.module.css'

function HashGlyph(): ReactElement {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M7.6 3.4 6 16.6M14 3.4l-1.6 13.2M3.5 7.6h13M2.8 12.4h13"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}

function label(room: Room): string {
  return room.name ?? room.slug ?? 'Channel'
}

function describeFailure(error: unknown): string {
  if (isApiError(error)) return error.message
  if (error instanceof Error && error.message !== '') return error.message
  return 'The channel could not be joined.'
}

export interface ChannelBrowserProps {
  open: boolean
  /**
   * Every open channel on this Space, joined or not. The ones already joined
   * are listed too, marked as such: a browser that hid them would leave the
   * reader unable to tell an empty result from a complete one.
   */
  channels: readonly Room[]
  onClose: () => void
  onNavigate?: () => void
}

/** Search and self-service entry for the open channels of this Space. */
export function ChannelBrowser({
  open,
  channels,
  onClose,
  onNavigate,
}: ChannelBrowserProps): ReactElement {
  const navigate = useNavigate()
  const toast = useToast()
  const { server } = useAuth()
  const joinChannel = useJoinChannel()
  const [query, setQuery] = useState('')
  const [failure, setFailure] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setQuery('')
    setFailure(null)
  }, [open])

  const spaceName = server?.display_name?.trim() || server?.server_id || 'this Space'

  const matches = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase()
    if (needle === '') return [...channels]

    return channels.filter((room) => {
      const haystack = `${label(room)} ${room.slug ?? ''} ${room.topic ?? ''}`
      return haystack.toLocaleLowerCase().includes(needle)
    })
  }, [channels, query])

  const view = useCallback(
    (room: Room): void => {
      navigate(`/rooms/${room.id}`)
      onNavigate?.()
      onClose()
    },
    [navigate, onClose, onNavigate],
  )

  const join = useCallback(
    async (room: Room): Promise<void> => {
      setFailure(null)
      try {
        const joined = await joinChannel.mutateAsync(room.id)
        toast.success(`You joined #${label(joined)}.`)
        navigate(`/rooms/${joined.id}`)
        onNavigate?.()
        onClose()
      } catch (error) {
        setFailure(describeFailure(error))
      }
    },
    [joinChannel, navigate, onClose, onNavigate, toast],
  )

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Browse channels"
      description="Find an open channel, preview it, or join so you can post."
      size="md"
    >
      <div className={styles.browser}>
        <TextField
          label="Filter channels"
          labelHidden
          type="search"
          value={query}
          placeholder="Filter channels"
          autoComplete="off"
          onChange={(event: ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
        />

        {failure !== null ? (
          <p className={styles.error} role="alert">
            {failure}
          </p>
        ) : null}

        {matches.length === 0 ? (
          <p className={styles.empty}>
            {channels.length === 0
              ? 'This Space has no open channels yet.'
              : 'No open channels matched your search.'}
          </p>
        ) : (
          <>
            <p className={styles.sectionLabel}>Open channels in {spaceName}</p>
            <ul className={styles.list} aria-label="Available channels">
            {matches.map((room) => {
              const joining = joinChannel.isPending && joinChannel.variables === room.id

              return (
                <li key={room.id} className={styles.channel}>
                  <button type="button" className={styles.identity} onClick={() => view(room)}>
                    <span className={styles.glyph} aria-hidden="true">
                      <HashGlyph />
                    </span>
                    <span className={styles.copy}>
                      <span className={styles.name}>{label(room)}</span>
                      {room.topic !== null && room.topic.trim() !== '' ? (
                        <span className={styles.meta}>{room.topic}</span>
                      ) : null}
                      <span className={styles.count}>
                        {`${room.member_count} ${room.member_count === 1 ? 'member' : 'members'}`}
                      </span>
                    </span>
                  </button>
                  {room.my_membership !== null ? (
                    <span className={styles.joined}>Joined</span>
                  ) : (
                    <Button
                      className={styles.join}
                      variant="primary"
                      size="sm"
                      loading={joining}
                      disabled={joinChannel.isPending && !joining}
                      onClick={() => void join(room)}
                    >
                      Join
                    </Button>
                  )}
                </li>
              )
            })}
          </ul>
          </>
        )}

        <p className={styles.footnote}>
          Private channels are not listed here, and never show faces. They exist only for the
          people who were added.
        </p>
      </div>
    </Modal>
  )
}
